import React from "react";
import { Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import FactorTable from "../components/FactorTable";
import OutcomesTable from "../components/OutcomesTable";
import Indicator from "../components/Indicator";

const OverviewPage = ({ data, overviewData, prevData, prevOverviewData }) => {
  // If data is empty or not provided
  if (!data || data.length === 0) {
    return <Text>No data available</Text>;
  }

  const filterLatestOutcomes = (data) => {
    if (!data || data.length === 0) return [];

    let latestEntries = {};

    data.forEach((entry) => {
      const outcomeTitle = entry.outcomeTitle || entry.name; // Ensure we're referencing the correct field

      if (!latestEntries[outcomeTitle]) {
        latestEntries[outcomeTitle] = entry;
      } else {
        // Replace if this entry is newer (assuming time-based ordering)
        latestEntries[outcomeTitle] = entry;
      }
    });

    // Return only the most recent entries
    return Object.values(latestEntries);
  };


  return (
    <Page style={styles.page}>
      {data.map((dimensionItem, index) => {
        const {
          dimensionTitle,
          dimensionAverage,
          factors,
          dimensionDiff: normDifferential,
        } = dimensionItem;
        // Extract the same data from the previous data using the index.
        const prevDimensionItem = prevData?.[index];
        const prevDimensionAverage = prevDimensionItem?.dimensionAverage || 0;
        const prevFactors = prevDimensionItem?.factors;

        const dimensionChange = dimensionAverage - prevDimensionAverage;
        // Prepare the factor rows for the FactorTable
        // Each factor in `factors` has shape: { factorTitle, average }
        const factorData = factors.map((factor, index) => {
          // Extract the same data from the previous data using the index.
          const prevFactor = prevFactors?.[index];
          const factorChange = factor?.average - prevFactor?.average;
          return {
            factor: factor.factorTitle,
            score: `${factor.average.toFixed(0)}%`,
            norm: factor?.normAverage
              ? `${(factor?.normAverage * 10)?.toFixed(1)}%`
              : "--", // Placeholder
            change: prevFactor ? `${factorChange?.toFixed(1)}%` : "--", // Placeholder
          };
        });


        return (
          <View key={index} style={styles.section}>
            {/* Dimension Title & Score */}
            <Text style={styles.title}>{dimensionTitle} Dimension Score:</Text>
            <View style={styles.scoreContainer}>
              <Text style={styles.score}>{dimensionAverage?.toFixed(0)}%</Text>
              <View style={styles.changeContainer}>
                {dimensionChange > 0 && (
                  <Indicator
                    label="CHANGE"
                    value={prevData ? dimensionChange?.toFixed(1) : 0}
                  />
                )}
                <Indicator
                  label="DIFF FROM NORM"
                  value={normDifferential?.toFixed(1)}
                />
              </View>
            </View>

            {/* Factor Table Section */}
            <View style={styles.tableSection}>
              <View style={styles.table}>
                <FactorTable data={factorData} />
              </View>
            </View>
          </View>
        );
      })}

      {/* Outcomes Overview Section */}
      <View style={styles.outcomesSection}>
        <Text style={styles.outcomesTitle}>Outcomes Overview</Text>
        <Text style={styles.outcomesSubtitle}>
          Summary metrics for your selected outcomes
        </Text>
        <Text style={styles.outcomesSummaryTitle}>Outcomes Summary</Text>
        <View style={styles.table}>
          <OutcomesTable
            data={filterLatestOutcomes(overviewData)}
            prevData={prevOverviewData}
          />
        </View>
      </View>
    </Page>
  );
};

// Styles for the Page
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#F8FAFF",
    padding: 40,
  },
  section: {
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    color: "#2A3039",
    fontWeight: 400,
    marginBottom: 10,
    fontFamily: "Outfit",
    letterSpacing: -0.4,
  },
  scoreContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  score: {
    fontSize: 36,
    color: "#2D70E2",
    fontWeight: 600,
    fontFamily: "Outfit",
    marginRight: 15,
    letterSpacing: -0.4,
  },
  changeContainer: {
    flexDirection: "row",
  },
  tableSection: {
    marginBottom: 20,
  },
  table: {
    borderWidth: 1,
    borderColor: "#EAFBF7",
  },
  outcomesSection: {
    marginTop: 30,
  },
  outcomesTitle: {
    fontSize: 28,
    color: "#2A3039",
    fontWeight: 400,
    marginBottom: 10,
    fontFamily: "Outfit",
    letterSpacing: -0.4,
  },
  outcomesSubtitle: {
    fontSize: 16,
    color: "#666D79",
    marginBottom: 20,
    fontFamily: "Raleway",
    fontWeight: 400,
  },
  outcomesSubtitleBold: {
    fontSize: 16,
    color: "#666D79",
    marginBottom: 20,
    fontFamily: "Raleway",
    fontWeight: 600,
  },
  outcomesSummaryTitle: {
    fontSize: 20,
    color: "#2D70E2",
    fontWeight: 400,
    fontFamily: "Outfit",
    marginBottom: 18,
    letterSpacing: -0.4,
  },
});

export default OverviewPage;